/**
 * Copyright (C) 2020 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

/*
  Class Description:

  This class renders the satellite indicator bubbles and handles their highlighting functionality.
 */

import React, { MouseEvent } from 'react';
import { arrayIndexToLetter } from '../../../../../utils/StringUtils';
import { getSatelliteStatusColor } from '../../../../../utils/TerminalUtilities';
import { isStandby } from '../../../../../utils/ConnectionStatusUtils';
import isNil from 'lodash/isNil';
import { SatelliteDisplayProps } from '../../TabbedPanel/TerminalTabPanel';

export interface SatelliteIndicatorProps extends SatelliteDisplayProps {}

function SatelliteIndicator({
  satelliteInFov,
  index,
  isHighlighted,
  isStickyMode,
  setHighlightedSatelliteInFov,
}: SatelliteIndicatorProps) {
  const handleSatelliteInFovIndicatorMouseEnter = () => {
    if (!isStickyMode) {
      setHighlightedSatelliteInFov({
        satelliteName: satelliteInFov.satelliteName,
        sticky: false,
        setByGraphicIndicator: true,
        setBySatelliteList: false,
      });
    }
  };

  const handleSatelliteInFovLabelMouseLeave = () => {
    if (!isStickyMode) {
      setHighlightedSatelliteInFov(undefined);
    }
  };

  const handleSatelliteClick = (event: MouseEvent) => {
    event.stopPropagation();
    setHighlightedSatelliteInFov({
      satelliteName: satelliteInFov.satelliteName,
      sticky: true,
      setByGraphicIndicator: true,
      setBySatelliteList: false,
    });
  };

  return (
    <svg data-test="satellite_indicator__svg" width="1200" height="1200" viewBox="0 0 1200 1200" xmlns="http://www.w3.org/2000/svg">
      {!isNil(satelliteInFov.azimuthDeg) ? (
        <g transform={`rotate(${satelliteInFov.azimuthDeg}, 600, 600)`}>
          {!satelliteInFov.isAlternative && (
            <line
              x1="600"
              x2="600"
              y1="610"
              y2="100"
              opacity={isHighlighted ? 1 : 0.6}
              strokeWidth="10"
              strokeDasharray={isStandby(satelliteInFov.connectivityStatus) ? '30 20' : ''}
              stroke={getSatelliteStatusColor(satelliteInFov)}
              strokeDashoffset="2"
            />
          )}
          <circle
            data-test="satellite_indicator__circle"
            cx="600"
            cy="100"
            r="81"
            opacity={isHighlighted ? 1 : 0.6}
            strokeWidth="4"
            stroke="#000000"
            fill={getSatelliteStatusColor(satelliteInFov)}
            onMouseEnter={handleSatelliteInFovIndicatorMouseEnter}
            onMouseLeave={handleSatelliteInFovLabelMouseLeave}
            onClick={handleSatelliteClick}
          />
          <text
            data-test="satellite_indicator__text"
            transform={`rotate(${360 - satelliteInFov.azimuthDeg}, 600, 100)`}
            x="602"
            y="139"
            opacity={isHighlighted ? 1 : 0.6}
            textAnchor="middle"
            fontSize="125"
            fontWeight="700"
            onMouseEnter={handleSatelliteInFovIndicatorMouseEnter}
            onClick={handleSatelliteClick}
          >
            {arrayIndexToLetter(index)}
          </text>
        </g>
      ) : null}
    </svg>
  );
}

export default SatelliteIndicator;
